import React, { useState, useEffect, useCallback, useMemo } from 'react';
// jspdf is lazy-loaded inside exportPDF to keep it off the initial bundle
// (~200KB gzip). Most users open the app without exporting a PDF.
import { fetchWithAuth } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency, getCurrentLocalDatetime, formatNumber, formatWeight, buildCSV } from '../../utils/helpers';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import SortSelector from '../common/SortSelector';
import ConfirmationModal from '../modals/ConfirmationModal';
import SalesAnalytics from '../analytics/SalesAnalytics';
import { 
  PlusCircle, 
  Edit, 
  ClipboardList, 
  FileText, 
  X,
  ShoppingCart,
  History
} from 'lucide-react';

// Audit `changes` is stored as a JSON string in the DB. Malformed/empty rows
// would otherwise throw inside render and blow up the whole audit modal.
function formatAuditChanges(raw) {
  if (raw == null || raw === '') return '(no details)';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return String(raw);
  }
}

function Sales({ user, setView, targetSaleId, clearTargetSale, onViewHarvests, handleLogout }) {
  const toast = useToast();
  const [sales, setSales] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [viewAuditSale, setViewAuditSale] = useState(null);
  const [audits, setAudits] = useState([]);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, tally: '' });
  const [archiveConfirm, setArchiveConfirm] = useState({ isOpen: false, ids: [], archived: false });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPaymentDetailId, setSelectedPaymentDetailId] = useState('');

  // Filter States
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    tallySheetNumber: '',
    buyer: '',
    heads: { op: 'eq', val: '' },
    kilos: { op: 'eq', val: '' },
    avgKilos: { op: 'eq', val: '' },
    totalSales: { op: 'eq', val: '' },
    avgPrice: { op: 'eq', val: '' },
    paid: 'all', // all, yes, no
    location: '',
    archived: 'unarchived' // unarchived, archived, all
  });

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const [newSale, setNewSale] = useState({
    date: getCurrentLocalDatetime(),
    tally_sheet_number: '',
    buyer: '',
    number_of_heads: '',
    kilos: '',
    unit_price: '',
    paid: false,
    notes: '',
    tally_sheet_picture: '',
    location_id: ''
  });

  // Each fetcher accepts an optional AbortSignal so the mount-effect can
  // cancel in-flight requests on unmount / dep change. Manual refetches
  // (handleArchive et al.) call without a signal and run uninterruptibly.
  const fetchSales = useCallback((signal) => {
    let url = '/sales';
    if (filters.startDate && filters.endDate) {
      const offset = new Date().getTimezoneOffset();
      url += `?start_date=${filters.startDate}&end_date=${filters.endDate}&offset=${offset}`;
    }
    return fetchWithAuth(url, { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setSales(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [filters.startDate, filters.endDate, user, handleLogout]);

  const fetchLocations = useCallback((signal) => {
    return fetchWithAuth('/locations', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setLocations(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  // Note: selectedPaymentDetailId is deliberately NOT a dep here. The
  // previous version included it, which caused this callback's identity
  // to change every time the user picked a payment method — re-firing
  // the mount-effect and re-fetching /payment_details on every click.
  // The "default to the first option" behavior now lives in its own
  // effect below.
  const fetchPaymentDetails = useCallback((signal) => {
    return fetchWithAuth('/payment_details', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setPaymentDetails(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    Promise.allSettled([
      fetchSales(controller.signal),
      fetchLocations(controller.signal),
      fetchPaymentDetails(controller.signal),
    ]).then(() => setIsLoading(false));
    return () => controller.abort();
  }, [fetchSales, fetchLocations, fetchPaymentDetails]);

  // Pick the first payment method once details load, if none selected yet.
  useEffect(() => {
    if (paymentDetails.length > 0 && !selectedPaymentDetailId) {
      setSelectedPaymentDetailId(paymentDetails[0].id.toString());
    }
  }, [paymentDetails, selectedPaymentDetailId]);

  useEffect(() => {
    if (targetSaleId && sales.length > 0) {
      const sale = sales.find(s => s.id === targetSaleId);
      if (sale) {
        handleViewClick(sale);
        if (clearTargetSale) clearTargetSale();
      }
    }
  }, [targetSaleId, sales, clearTargetSale]);

  const handleInputChange = (e, isEditing = false) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (isEditing) {
      setEditingSale({ ...editingSale, [name]: val });
    } else {
      setNewSale({ ...newSale, [name]: val });
    }
  };

  const handleFileChange = (e, isEditing = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEditing) {
          setEditingSale({ ...editingSale, tally_sheet_picture: reader.result });
        } else {
          setNewSale({ ...newSale, tally_sheet_picture: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFilterChange = (field, value, subField = null) => {
    if (subField) {
      setFilters(prev => ({ ...prev, [field]: { ...prev[field], [subField]: value } }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedSales.map(s => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Archive flow: clicking Archive/Restore opens a styled confirmation
  // modal (was: native browser confirm). The actual request runs in
  // performArchive once the user confirms.
  const handleArchive = (ids, archivedStatus) => {
    if (!ids || ids.length === 0) return;
    setArchiveConfirm({ isOpen: true, ids, archived: archivedStatus });
  };

  const performArchive = () => {
    const { ids, archived: archivedStatus } = archiveConfirm;
    setArchiveConfirm({ isOpen: false, ids: [], archived: false });

    fetchWithAuth('/archive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'sales',
        ids: ids,
        archived: archivedStatus
      })
    }, user, handleLogout)
    .then(res => {
      if (!res.ok) throw new Error('Failed to update archive status');
      fetchSales();
      setSelectedIds([]);
      toast.success(archivedStatus ? 'Records archived.' : 'Records restored.');
    })
    .catch(err => toast.error(err.message));
  };

  // Reset to first page when filters, rows per page or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rowsPerPage, sortConfig]);

  // Filter Logic
  const filteredSales = useMemo(() => {
    return sales.filter(s => {
      // Date Range
      if (filters.startDate && filters.endDate) {
        const saleDate = new Date(s.date).toISOString().split('T')[0];
        if (saleDate < filters.startDate || saleDate > filters.endDate) return false;
      }

      // Tally Sheet Number
      if (filters.tallySheetNumber && !s.tally_sheet_number.toLowerCase().includes(filters.tallySheetNumber.toLowerCase())) return false;

      // Buyer
      if (filters.buyer && !s.buyer.toLowerCase().includes(filters.buyer.toLowerCase())) return false;

      // Location
      if (filters.location && (!s.location_name || !s.location_name.toLowerCase().includes(filters.location.toLowerCase()))) return false;

      // Paid
      if (filters.paid !== 'all') {
        const isPaid = filters.paid === 'yes';
        if (s.paid !== isPaid) return false;
      }

      // Archive status
      if (filters.archived === 'unarchived' && s.archived) return false;
      if (filters.archived === 'archived' && !s.archived) return false;

      // Numeric Filters Helper
      const checkNumeric = (val, filter) => {
        if (filter.val === '') return true;
        const numVal = parseFloat(filter.val);
        if (filter.op === 'gt') return val > numVal;
        if (filter.op === 'lt') return val < numVal;
        return val === numVal;
      };

      if (!checkNumeric(s.number_of_heads, filters.heads)) return false;
      if (!checkNumeric(s.kilos, filters.kilos)) return false;
      if (!checkNumeric(s.total_sales, filters.totalSales)) return false;
      
      const avgK = s.kilos / s.number_of_heads;
      if (!checkNumeric(avgK, filters.avgKilos)) return false;

      const avgP = s.total_sales / s.kilos;
      if (!checkNumeric(avgP, filters.avgPrice)) return false;

      return true;
    });
  }, [sales, filters]);

  // Sorting Logic
  const sortedSales = useMemo(() => {
    let sortableItems = [...filteredSales];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'location') {
          aVal = (a.location_name || '').toLowerCase();
          bVal = (b.location_name || '').toLowerCase();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredSales, sortConfig]);

  const paginatedSales = useMemo(() => {
    if (rowsPerPage === 'all') return sortedSales;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedSales.slice(start, start + rowsPerPage);
  }, [sortedSales, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const [auditSortConfig, setAuditSortConfig] = useState({ key: 'changed_at', direction: 'desc' });

  const sortedAudits = useMemo(() => {
    let sortableItems = [...audits];
    if (auditSortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[auditSortConfig.key];
        let bVal = b[auditSortConfig.key];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return auditSortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return auditSortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [audits, auditSortConfig]);

  const requestAuditSort = (key) => {
    let direction = 'asc';
    if (auditSortConfig.key === key && auditSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setAuditSortConfig({ key, direction });
  };

  const exportCSV = () => {
    const headers = ['Date', 'Tally #', 'Buyer', 'Heads', 'Kilos', 'Unit Price', 'Total (PHP)', 'Paid', 'Location', 'Notes'];
    const rows = sortedSales.map(s => [
      new Date(s.date).toLocaleString(),
      s.tally_sheet_number,
      s.buyer,
      s.number_of_heads,
      s.kilos,
      s.unit_price,
      s.total_sales,
      s.paid ? 'Yes' : 'No',
      s.location_name || '',
      s.notes || ''
    ]);

    const csvContent = buildCSV([headers, ...rows]);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `sales_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const doc = new jsPDF('landscape');
    doc.text("Animal Sales Report", 14, 15);
    
    const tableColumn = ["Date", "Tally #", "Buyer", "Heads", "Kilos", "Unit Price", "Total (PHP)", "Paid", "Location"];
    const tableRows = sortedSales.map(s => [
      new Date(s.date).toLocaleString(),
      s.tally_sheet_number,
      s.buyer,
      s.number_of_heads,
      s.kilos.toFixed(2),
      s.unit_price.toFixed(2),
      s.total_sales.toFixed(2),
      s.paid ? 'Yes' : 'No',
      s.location_name || '-'
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80] },
      styles: { fontSize: 7 },
      footStyles: { fillColor: [248, 249, 250], textColor: [44, 62, 80], fontStyle: 'bold' }
    });
    doc.save(`sales_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    const total_sales = parseFloat(newSale.kilos) * parseFloat(newSale.unit_price);

    const localDate = new Date(newSale.date);
    const isoDate = localDate.toISOString();

    fetchWithAuth('/sales/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...newSale,
        date: isoDate,
        number_of_heads: parseInt(newSale.number_of_heads) || 0,
        kilos: parseFloat(newSale.kilos) || 0,
        unit_price: parseFloat(newSale.unit_price) || 0,
        total_sales: total_sales || 0,
        location_id: newSale.location_id ? parseInt(newSale.location_id) : 0
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
      }
      return res.json();
    })
    .then(() => {
      fetchSales();
      setShowRecordModal(false);
      setNewSale({
        date: getCurrentLocalDatetime(),
        tally_sheet_number: '',
        buyer: '',
        number_of_heads: '',
        kilos: '',
        unit_price: '',
        paid: false,
        notes: '',
        tally_sheet_picture: '',
        location_id: ''
      });
      setError('');
    })
    .catch(err => {
      setError(err.message);
    })
    .finally(() => setIsSubmitting(false));
  };

  const handleViewClick = (sale) => {
    setViewingSale(sale);
    setError('');
    setEditingSale(null);
  };

  const handleEditClick = (sale) => {
    // Format date for datetime-local input
    const localDate = new Date(sale.date);
    localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
    const dateStr = localDate.toISOString().slice(0, 16);

    setEditingSale({ ...sale, date: dateStr, location_id: sale.location_id || '', archived: sale.archived || false });
    setError('');
    setViewingSale(null);
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    const total_sales = parseFloat(editingSale.kilos) * parseFloat(editingSale.unit_price);

    const localDate = new Date(editingSale.date);
    const isoDate = localDate.toISOString();

    fetchWithAuth('/sales/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editingSale,
        date: isoDate,
        number_of_heads: parseInt(editingSale.number_of_heads) || 0,
        kilos: parseFloat(editingSale.kilos) || 0,
        unit_price: parseFloat(editingSale.unit_price) || 0,
        total_sales: total_sales || 0,
        location_id: editingSale.location_id ? parseInt(editingSale.location_id) : 0,
        archived: !!editingSale.archived
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
      }
      return res.json();
    })
    .then(() => {
      fetchSales();
      setEditingSale(null);
    })
    .catch(err => {
      setError(err.message);
    })
    .finally(() => setIsSubmitting(false));
  };

  const handleAuditClick = (sale) => {
    setViewAuditSale(sale);
    setEditingSale(null);
    fetchWithAuth(`/sales/audits?sale_id=${sale.id}`, {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setAudits(data || []));
  };

  const handleDeleteClick = (sale) => {
    setDeleteConfirm({ isOpen: true, id: sale.id, tally: sale.tally_sheet_number });
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    setError('');
    fetchWithAuth(`/sales/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchSales();
      setDeleteConfirm({ isOpen: false, id: null, tally: '' });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null, tally: '' });
    });
  };

  const totalHeads = filteredSales.reduce((sum, s) => sum + s.number_of_heads, 0);
  const totalWeight = filteredSales.reduce((sum, s) => sum + s.kilos, 0);
  const totalRevenue = filteredSales.reduce((sum, s) => sum + s.total_sales, 0);

  const generateInvoiceText = () => {
    const selectedSales = sortedSales.filter(s => selectedIds.includes(s.id));
    const payment = paymentDetails.find(p => p.id.toString() === selectedPaymentDetailId);
    
    let text = (payment?.invoice_header || "ANIMAL SALES INVOICE") + "\n";
    text += "=============================\n\n";
    
    let grandTotal = 0;
    selectedSales.forEach(s => {
      text += `Date: ${new Date(s.date).toLocaleDateString()}\n`;
      text += `Tally Sheet #: ${s.tally_sheet_number}\n`;
      text += `Buyer: ${s.buyer}\n`;
      text += `Heads: ${formatNumber(s.number_of_heads)}\n`;
      text += `Weight: ${formatWeight(s.kilos)}\n`;
      text += `Unit Price: ${formatCurrency(s.unit_price)}/kg\n`;
      text += `Total: ${formatCurrency(s.total_sales)}\n`;
      text += "=============================\n";
      grandTotal += s.total_sales;
    });

    text += `\nGRAND TOTAL: ${formatCurrency(grandTotal)}\n\n`;

    if (payment) {
      text += "PAYMENT DETAILS\n";
      text += "=============================\n";
      text += `${payment.details}\n`;
      text += "=============================\n";
    }

    return text;
  };

  const copyInvoiceToClipboard = () => {
    const text = generateInvoiceText();
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Invoice copied to clipboard"))
      .catch(err => toast.error("Could not copy: " + (err?.message || err)));
  };

  return (
    <div className="sales-view">
      <div className="view-header">
        <h2>Animal Sales</h2>
        <div className="view-actions">
          <button className="secondary-btn no-print" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="submit-btn no-print" onClick={() => setShowRecordModal(true)}>
            Add Sales Record
          </button>
          <button className="secondary-btn no-print" onClick={() => setShowAnalytics(!showAnalytics)}>
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          <button className="action-btn no-print" onClick={exportCSV}>
            Download CSV
          </button>
          <button className="action-btn no-print" onClick={exportPDF}>
            Download PDF
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <h4>Filters</h4>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range-inputs">
                <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
                <span>to</span>
                <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
              </div>
            </div>

            <div className="filter-group">
              <label>Tally Sheet #</label>
              <input placeholder="Search tally..." value={filters.tallySheetNumber} onChange={(e) => handleFilterChange('tallySheetNumber', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Buyer</label>
              <input placeholder="Search buyer..." value={filters.buyer} onChange={(e) => handleFilterChange('buyer', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Location</label>
              <input 
                list="location-options"
                placeholder="Search location..." 
                value={filters.location} 
                onChange={(e) => handleFilterChange('location', e.target.value)} 
              />
              <datalist id="location-options">
                {locations.map(l => <option key={l.id} value={l.name} />)}
              </datalist>
            </div>

            <div className="filter-group">
              <label>Paid Status</label>
              <select value={filters.paid} onChange={(e) => handleFilterChange('paid', e.target.value)}>
                <option value="all">All</option>
                <option value="yes">Paid Only</option>
                <option value="no">Unpaid Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Archive Status</label>
              <select value={filters.archived} onChange={(e) => handleFilterChange('archived', e.target.value)}>
                <option value="unarchived">Unarchived Only</option>
                <option value="archived">Archived Only</option>
                <option value="all">All Records</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Heads</label>
              <div className="numeric-filter">
                <select value={filters.heads.op} onChange={(e) => handleFilterChange('heads', e.target.value, 'op')}>
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="lt">&lt;</option>
                </select>
                <input type="number" placeholder="Value" value={filters.heads.val} onChange={(e) => handleFilterChange('heads', e.target.value, 'val')} />
              </div>
            </div>

            <div className="filter-group">
              <label>Total Sales</label>
              <div className="numeric-filter">
                <select value={filters.totalSales.op} onChange={(e) => handleFilterChange('totalSales', e.target.value, 'op')}>
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="lt">&lt;</option>
                </select>
                <input type="number" placeholder="Value" value={filters.totalSales.val} onChange={(e) => handleFilterChange('totalSales', e.target.value, 'val')} />
              </div>
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={() => setFilters({
              startDate: '', endDate: '', tallySheetNumber: '', buyer: '',
              heads: { op: 'eq', val: '' }, kilos: { op: 'eq', val: '' },
              avgKilos: { op: 'eq', val: '' }, totalSales: { op: 'eq', val: '' },
              avgPrice: { op: 'eq', val: '' }, paid: 'all', location: ''
            })} className="secondary-btn">Clear All Filters</button>
          </div>
        </div>
      )}

      {showAnalytics && (
        <SalesAnalytics sales={filteredSales} />
      )}

      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'date', label: 'Date' },
                { key: 'tally_sheet_number', label: 'Tally #' },
                { key: 'buyer', label: 'Buyer' },
                { key: 'number_of_heads', label: 'Heads' },
                { key: 'kilos', label: 'Weight' },
                { key: 'total_sales', label: 'Total Revenue' },
                { key: 'location', label: 'Location' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {isLoading && <div className="loading-banner">Loading sales…</div>}

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th><input
                  type="checkbox"
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < paginatedSales.length; }}
                  onChange={handleSelectAll}
                  checked={paginatedSales.length > 0 && selectedIds.length === paginatedSales.length}
                /></th>
                <th onClick={() => requestSort('date')} className="sortable">Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('tally_sheet_number')} className="sortable">Tally # {sortConfig.key === 'tally_sheet_number' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('buyer')} className="sortable">Buyer {sortConfig.key === 'buyer' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('number_of_heads')} className="sortable text-right">Heads {sortConfig.key === 'number_of_heads' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('kilos')} className="sortable text-right">Weight {sortConfig.key === 'kilos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('unit_price')} className="sortable text-right">Unit Price {sortConfig.key === 'unit_price' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('total_sales')} className="sortable text-right">Total {sortConfig.key === 'total_sales' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('location')} className="sortable">Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSales.map(s => (
                <tr key={s.id} onClick={() => handleViewClick(s)} className={`clickable-row ${s.archived ? 'archived-row' : ''} ${selectedIds.includes(s.id) ? 'selected-row' : ''}`}>
                  <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => handleSelectOne(s.id)} /></td>
                  <td>{new Date(s.date).toLocaleDateString()} {s.archived && <span className="archived-tag">Archived</span>}</td>
                  <td>{s.tally_sheet_number}</td>
                  <td>{s.buyer}</td>
                  <td className="text-right">{formatNumber(s.number_of_heads)}</td>
                  <td className="text-right">{formatWeight(s.kilos)}</td>
                  <td className="text-right">{formatCurrency(s.unit_price)}</td>
                  <td className={`text-right ${s.paid ? 'status-paid' : 'status-unpaid'}`}>{formatCurrency(s.total_sales)}</td>
                  <td>{s.location_name || '-'}</td>
                  <td onClick={(event) => event.stopPropagation()} className="row-actions">
                    <button className="action-btn sm" onClick={() => onViewHarvests(s.tally_sheet_number)}>Agents</button>
                    <button className="action-btn sm" onClick={() => handleAuditClick(s)}>Audit</button>
                    <button className="action-btn sm" onClick={() => handleEditClick(s)}>Edit</button>
                    <button className="secondary-btn sm" onClick={() => handleArchive([s.id], !s.archived)}>{s.archived ? 'Restore' : 'Archive'}</button>
                    {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDeleteClick(s)}>Del</button>}
                  </td>
                </tr>
              ))}
              <tr className="totals-row">
                <td colSpan="4">Totals</td>
                <td className="text-right highlight"><strong>{formatNumber(totalHeads)}</strong></td>
                <td className="text-right"><strong>{formatWeight(totalWeight)}</strong></td>
                <td></td>
                <td className="text-right highlight"><strong>{formatCurrency(totalRevenue)}</strong></td>
                <td colSpan="2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedSales.map(s => (
            <div key={s.id} className={`data-card ${s.archived ? 'archived-row' : ''} ${selectedIds.includes(s.id) ? 'selected-card' : ''}`} onClick={() => handleViewClick(s)}>
              <div className="card-header">
                <div className="card-title-group">
                  <input type="checkbox" checked={selectedIds.includes(s.id)} onClick={e => e.stopPropagation()} onChange={() => handleSelectOne(s.id)} />
                  <h4>{s.buyer} {s.archived && <span className="archived-tag">Archived</span>}</h4>
                </div>
                <span className="card-date">{new Date(s.date).toLocaleDateString()}</span>
              </div>
              <div className="card-body">
                <div className="card-item"><span className="label">Tally #</span><span className="value">{s.tally_sheet_number}</span></div>
                <div className="card-item"><span className="label">Heads</span><span className="value">{formatNumber(s.number_of_heads)}</span></div>
                <div className="card-item"><span className="label">Weight</span><span className="value">{formatWeight(s.kilos)}</span></div>
                <div className="card-item"><span className="label">Total</span><span className={`value highlight ${s.paid ? 'status-paid' : 'status-unpaid'}`}>{formatCurrency(s.total_sales)}</span></div>
                <div className="card-item"><span className="label">Location</span><span className="value">{s.location_name || '-'}</span></div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="action-btn sm" onClick={() => onViewHarvests(s.tally_sheet_number)}>Agents</button>
                <button className="action-btn sm" onClick={() => handleAuditClick(s)}>Audit</button>
                <button className="action-btn sm" onClick={() => handleEditClick(s)}>Edit</button>
                <button className="secondary-btn sm" onClick={() => handleArchive([s.id], !s.archived)}>{s.archived ? 'Restore' : 'Archive'}</button>
                {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDeleteClick(s)}>Del</button>}
              </div>
            </div>
          ))}
          <div className="data-card summary-card">
            <h4>Totals Summary</h4>
            <div className="card-body">
              <div className="card-item"><span className="label">Total Records</span><span className="value">{formatNumber(filteredSales.length)}</span></div>
              <div className="card-item"><span className="label">Total Heads</span><span className="value highlight">{formatNumber(totalHeads)}</span></div>
              <div className="card-item"><span className="label">Total Weight</span><span className="value">{formatWeight(totalWeight)}</span></div>
              <div className="card-item"><span className="label">Total Revenue</span><span className="value highlight status-paid">{formatCurrency(totalRevenue)}</span></div>
            </div>
          </div>
        </div>
      )}

      <Pagination 
        totalItems={filteredSales.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar no-print">
          <span className="selection-count">{selectedIds.length} items selected</span>
          <div className="bulk-actions">
            <button className="action-btn" onClick={() => setShowInvoiceModal(true)}>Generate Invoice</button>
            {filters.archived !== 'archived' && <button className="secondary-btn" onClick={() => handleArchive(selectedIds, true)}>Archive Selected</button>}
            {filters.archived !== 'unarchived' && <button className="submit-btn" onClick={() => handleArchive(selectedIds, false)}>Restore Selected</button>}
            <button className="cancel-btn" onClick={() => setSelectedIds([])}>Clear Selection</button>
          </div>
        </div>
      )}

      {showRecordModal && (
        <div className="modal" onClick={() => { setShowRecordModal(false); setEditingSale(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <PlusCircle size={24} />
                <h3>Add New Sales Record</h3>
              </div>
              <button className="close-btn" onClick={() => { setShowRecordModal(false); setEditingSale(null); setError(''); }}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Date & Time</label>
                <input name="date" type="datetime-local" value={newSale.date} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Tally Sheet Number</label>
                <input name="tally_sheet_number" value={newSale.tally_sheet_number} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Buyer Name</label>
                <input name="buyer" value={newSale.buyer} onChange={handleInputChange} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Heads</label>
                  <input name="number_of_heads" type="number" value={newSale.number_of_heads} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Total Kilos</label>
                  <input name="kilos" type="number" step="0.01" value={newSale.kilos} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Unit Price (PHP/kg)</label>
                  <input name="unit_price" type="number" step="0.01" value={newSale.unit_price} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <select name="location_id" value={newSale.location_id} onChange={handleInputChange} required>
                    <option value="">Select Location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input name="paid" type="checkbox" checked={newSale.paid} onChange={handleInputChange} />
                  Mark as Paid
                </label>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input name="notes" value={newSale.notes} onChange={handleInputChange} placeholder="Optional notes" />
              </div>
              <div className="form-group">
                <label>Tally Sheet Picture</label>
                <input type="file" accept="image/*" onChange={handleFileChange} />
              </div>
              {newSale.tally_sheet_picture && (
                <div className="image-preview">
                  <img src={newSale.tally_sheet_picture} alt="Preview" />
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Save Record'}</button>
                <button type="button" className="secondary-btn" onClick={() => { setShowRecordModal(false); setEditingSale(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingSale && (
        <div className="modal" onClick={() => { setEditingSale(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Edit size={24} />
                <h3>Edit Sales Record</h3>
              </div>
              <button className="close-btn" onClick={() => { setEditingSale(null); setError(''); }}><X size={20} /></button>
            </div>
            <div style={{marginBottom: '1.5rem', color: 'var(--google-grey-600)', fontSize: '0.9rem'}}>
              Updating record for Tally <strong>#{editingSale.tally_sheet_number}</strong>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleUpdateSubmit}>
              <div className="form-group">
                <label>Date & Time</label>
                <input name="date" type="datetime-local" value={editingSale.date} onChange={(e) => handleInputChange(e, true)} required />
              </div>
              <div className="form-group">
                <label>Tally Sheet Number</label>
                <input name="tally_sheet_number" value={editingSale.tally_sheet_number} onChange={(e) => handleInputChange(e, true)} required />
              </div>
              <div className="form-group">
                <label>Buyer Name</label>
                <input name="buyer" value={editingSale.buyer} onChange={(e) => handleInputChange(e, true)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Heads</label>
                  <input name="number_of_heads" type="number" value={editingSale.number_of_heads} onChange={(e) => handleInputChange(e, true)} required />
                </div>
                <div className="form-group">
                  <label>Total Kilos</label>
                  <input name="kilos" type="number" step="0.01" value={editingSale.kilos} onChange={(e) => handleInputChange(e, true)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Unit Price (PHP/kg)</label>
                  <input name="unit_price" type="number" step="0.01" value={editingSale.unit_price} onChange={(e) => handleInputChange(e, true)} required />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <select name="location_id" value={editingSale.location_id} onChange={(e) => handleInputChange(e, true)} required>
                    <option value="">Select Location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input name="paid" type="checkbox" checked={editingSale.paid} onChange={(e) => handleInputChange(e, true)} />
                    Mark as Paid
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input name="archived" type="checkbox" checked={editingSale.archived} onChange={(e) => handleInputChange(e, true)} />
                    Archived
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input name="notes" value={editingSale.notes} onChange={(e) => handleInputChange(e, true)} />
              </div>
              <div className="form-group">
                <label>Tally Sheet Picture</label>
                <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, true)} />
              </div>
              {editingSale.tally_sheet_picture && (
                <div className="image-preview">
                  <img src={editingSale.tally_sheet_picture} alt="Preview" />
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Update Record'}</button>
                <button type="button" className="secondary-btn" onClick={() => { setEditingSale(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewAuditSale && (
        <div className="modal" onClick={() => setViewAuditSale(null)}>
          <div className="modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <ClipboardList size={24} />
                <h3>Audit Log <span className="highlight" style={{marginLeft: '0.5rem'}}>#{viewAuditSale.tally_sheet_number}</span></h3>
              </div>
              <button className="close-btn" onClick={() => setViewAuditSale(null)}><X size={20} /></button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => requestAuditSort('changed_at')} className="sortable">Date {auditSortConfig.key === 'changed_at' && (auditSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th onClick={() => requestAuditSort('changed_by')} className="sortable">User {auditSortConfig.key === 'changed_by' && (auditSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th onClick={() => requestAuditSort('action')} className="sortable">Action {auditSortConfig.key === 'action' && (auditSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAudits.length === 0 ? (
                    <tr><td colSpan="4" className="text-center">No audit logs found.</td></tr>
                  ) : (
                    sortedAudits.map(a => (
                      <tr key={a.id}>
                        <td>{new Date(a.changed_at).toLocaleString()}</td>
                        <td>{a.changed_by}</td>
                        <td>{a.action}</td>
                        <td>
                          <pre className="audit-changes">
                            {formatAuditChanges(a.changes)}
                          </pre>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button onClick={() => setViewAuditSale(null)} className="secondary-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {viewingSale && (
        <div className="modal" onClick={() => setViewingSale(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <ShoppingCart size={24} />
                <h3>Sale Details</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button className="action-btn sm" onClick={() => { setViewingSale(null); onViewHarvests(viewingSale.tally_sheet_number); }}>
                  <History size={14} style={{marginRight: '6px'}} /> Agents
                </button>
                <button className="action-btn sm" onClick={() => handleEditClick(viewingSale)}>
                  <Edit size={14} style={{marginRight: '6px'}} /> Edit
                </button>
                <button className="close-btn" onClick={() => setViewingSale(null)}><X size={20} /></button>
              </div>
            </div>
            <div className="details-grid">
              <div className="details-column">
                <p><strong>Date:</strong> {new Date(viewingSale.date).toLocaleString()}</p>
                <p><strong>Tally #:</strong> {viewingSale.tally_sheet_number}</p>
                <p><strong>Buyer:</strong> {viewingSale.buyer}</p>
                <p><strong>Location:</strong> {viewingSale.location_name || '-'}</p>
              </div>
              <div className="details-column">
                <p><strong>Heads:</strong> {viewingSale.number_of_heads}</p>
                <p><strong>Weight:</strong> {viewingSale.kilos.toFixed(2)} kg</p>
                <p><strong>Unit Price:</strong> {formatCurrency(viewingSale.unit_price)}</p>
                <p><strong>Total:</strong> <span className="highlight status-paid">{formatCurrency(viewingSale.total_sales)}</span></p>
                <p><strong>Status:</strong> <span className={viewingSale.paid ? 'status-paid' : 'status-unpaid'}>{viewingSale.paid ? 'Paid' : 'Unpaid'}</span></p>
              </div>
            </div>
            {viewingSale.notes && (
              <div className="notes-section">
                <strong>Notes:</strong>
                <p>{viewingSale.notes}</p>
              </div>
            )}
            {viewingSale.tally_sheet_picture && (
              <div className="picture-section">
                <strong>Tally Sheet Picture:</strong>
                <div className="image-container">
                  <img src={viewingSale.tally_sheet_picture} alt="Tally Sheet" onClick={() => setViewImage(viewingSale.tally_sheet_picture)} />
                </div>
              </div>
            )}
            <div className="form-actions">
              <button onClick={() => setViewingSale(null)} className="secondary-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {viewImage && (
        <div className="image-modal" onClick={() => setViewImage(null)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <img src={viewImage} alt="Full Size" />
            <div className="modal-actions">
              <button className="secondary-btn" onClick={() => setViewImage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Sales Record"
        message={`Are you sure you want to delete tally sheet #${deleteConfirm.tally}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, tally: '' })}
      />

      <ConfirmationModal
        isOpen={archiveConfirm.isOpen}
        title={archiveConfirm.archived ? 'Archive Records' : 'Restore Records'}
        message={(archiveConfirm.archived
          ? 'Archive '
          : 'Restore ') + (archiveConfirm.ids.length === 1
            ? 'this record?'
            : `these ${archiveConfirm.ids.length} records?`)}
        confirmText={archiveConfirm.archived ? 'Archive' : 'Restore'}
        confirmClass={archiveConfirm.archived ? 'secondary-btn' : 'submit-btn'}
        onConfirm={performArchive}
        onCancel={() => setArchiveConfirm({ isOpen: false, ids: [], archived: false })}
      />

      {showInvoiceModal && (
        <div className="modal" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div className="modal-title-group">
                <FileText size={24} />
                <h3>Generate Invoice</h3>
              </div>
              <button className="close-btn" onClick={() => setShowInvoiceModal(false)}><X size={20} /></button>
            </div>

            <div className="form-group">
              <label>Select Payment Method</label>
              <select 
                value={selectedPaymentDetailId} 
                onChange={(e) => setSelectedPaymentDetailId(e.target.value)}
              >
                <option value="">-- No Payment Details --</option>
                {paymentDetails.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Invoice Preview</label>
              <div className="invoice-preview-container">
                {generateInvoiceText()}
              </div>
            </div>

            <div className="form-actions dual">
              <button onClick={copyInvoiceToClipboard} className="submit-btn">Copy to Clipboard</button>
              <button onClick={() => setShowInvoiceModal(false)} className="secondary-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sales;
